import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, ArrowRight, ChevronUp } from 'lucide-react';
import SEO from '../../components/SEO';

const ExpandableCard = ({ number, title, subtitle, description, bullets, outcome, mediaSrc, mediaType }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="bg-white border-2 border-purple-200 rounded-2xl overflow-hidden hover:border-purple-400 transition-all duration-300 shadow-lg hover:shadow-xl min-w-0"
      onMouseEnter={() => setIsOpen(true)}
    >
      <button
        onClick={() => setIsOpen(true)}
        className="w-full text-left p-6 sm:p-8 flex items-start gap-4 cursor-pointer group"
      >
        <span className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mt-1">{number}.</span>
        <div className="flex-1 min-w-0">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800 group-hover:text-purple-600 transition-colors">
            {title}
          </h3>
          <p className="text-purple-500 mt-1 text-base font-medium">{subtitle}</p>
          <p className="text-gray-600 mt-3 text-lg leading-relaxed">{description}</p>
        </div>
        <ChevronDown
          className={`w-7 h-7 text-purple-500 flex-shrink-0 mt-2 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      <div
        className={`transition-all duration-500 ease-in-out overflow-hidden ${isOpen ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'}`}
      >
        <div className="px-6 sm:px-8 pb-8 space-y-6">
          <button
            onClick={() => setIsOpen(false)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-purple-600 hover:text-purple-700 transition-colors"
            aria-label="Collapse section"
          >
            <ChevronUp className="w-4 h-4" />
            Retract
          </button>

          <div className="border-t border-purple-200 pt-6"></div>

          {bullets && bullets.length > 0 && (
            <ul className="space-y-3">
              {bullets.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <ArrowRight className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          )}

          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
            <h4 className="text-lg font-semibold text-purple-600 mb-2">Outcome</h4>
            <p className="text-gray-700 leading-relaxed">{outcome}</p>
          </div>

          {mediaSrc && (
            <div className="rounded-xl overflow-hidden border-2 border-purple-200 bg-gray-50">
              {mediaType === 'video' ? (
                <video src={`/images/${mediaSrc}`} controls className="w-full">
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img src={`/images/${mediaSrc}`} alt={title} className="w-full object-cover" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConstructionTech = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const whatWeDo = [
    {
      number: 1,
      title: 'Planning & Regulatory Intelligence',
      subtitle: 'Automating Development Control & Site Analysis',
      description: 'We develop platforms that integrate with national mapping systems (e.g., OneMap) and planning frameworks to automatically retrieve and interpret site-specific regulatory constraints.',
      bullets: [
        'Zoning and land use controls',
        'Plot ratio and GFA constraints',
        'Building height and setback requirements',
        'Envelope controls and special planning areas',
        'Heritage and conservation overlays'
      ],
      outcome: 'Faster feasibility studies, reduced compliance risk, and earlier validation against URA and local authority guidelines.',
      mediaSrc: 'constech1.png',
      mediaType: 'image'
    },
    {
      number: 2,
      title: 'AI-Assisted Generative Design',
      subtitle: 'Rapid Concept Development within Regulatory Constraints',
      description: 'We build intelligent design systems that generate layout options compliant by design, supporting residential, commercial, industrial, and mixed-use projects.',
      bullets: [
        'Site boundary conditions',
        'Development control parameters',
        'Building typology requirements',
        'Unit mix or operational constraints'
      ],
      outcome: 'Concept design reduced from hours or days to minutes — with options that are compliant by design.',
      mediaSrc: 'constech2.png',
      mediaType: 'image'
    },
    {
      number: 3,
      title: 'BIM Automation & Digital Delivery',
      subtitle: 'From Parametric Inputs to Coordinated BIM Models',
      description: 'We develop automation pipelines connecting frontend planning tools with downstream BIM, procurement, and fabrication environments.',
      bullets: [
        'Convert structured design data into BIM models',
        'Generate coordinated drawings and schedules',
        'Support IDD workflows and digital submissions',
        'Integrate with fabrication and DfMA processes'
      ],
      outcome: 'Improved model accuracy, reduced manual drafting effort, and seamless transition from concept to construction documentation.',
      mediaSrc: 'constech3.MP4',
      mediaType: 'video'
    },
    {
      number: 4,
      title: 'Intelligent Project & ERP Automation',
      subtitle: 'Workflow Orchestration for Construction Enterprises',
      description: 'Construction and real estate organisations operate across procurement, contracts, finance, HR, and site operations. Traditional ERP systems are rigid and costly to modify.',
      bullets: [
        'Digitise approval processes',
        'Automate procurement and contract routing',
        'Monitor SLA and compliance thresholds',
        'Integrate with existing ERP and accounting systems'
      ],
      outcome: 'Reduced process turnaround time, improved governance, and greater operational transparency.',
      mediaSrc: 'constech4.png',
      mediaType: 'image'
    },
    {
      number: 5,
      title: 'AI-Powered Decision Support',
      subtitle: 'Smart Automation Across the Building Lifecycle',
      description: 'We embed AI into operational workflows to support decision-making. Every decision is traceable and auditable — supporting governance requirements across regulated environments.',
      bullets: [
        'Procurement approvals and anomaly detection',
        'Cost forecasting and budget tracking',
        'Document classification and contract review',
        'Exception handling and case-based retrieval',
        'Asset performance analytics'
      ],
      outcome: 'Higher straight-through processing, faster approvals, and reduced manual workload.',
      mediaSrc: 'constech5.png',
      mediaType: 'image'
    }
  ];

  const solutionTabs = [
    {
      title: 'Digital Developer Platforms',
      description: 'Feasibility tools, planning intelligence, generative design, and portfolio analytics for property developers.'
    },
    {
      title: 'Contractor Digitalisation',
      description: 'Project workflow automation, BIM coordination, procurement orchestration, and site analytics.'
    },
    {
      title: 'BIM & IDD Enablement',
      description: 'Cloud-based collaboration, model coordination, 4D scheduling, and digital submission readiness.'
    },
    {
      title: 'DfMA & Fabrication Integration',
      description: 'Parametric modelling, module standardisation, and digital-to-manufacturing workflows.'
    },
    {
      title: 'Smart Facilities & Asset Management',
      description: 'IoT integration, predictive maintenance, condition monitoring, and lifecycle performance analytics.'
    }
  ];

  const initiatives = [
    'BCA Integrated Digital Delivery (IDD)',
    'CORENET X digital submissions',
    'DfMA and Design for Manufacturing & Assembly',
    'Smart Nation & GovTech digital integration',
    'BIM-based project delivery',
    'ESG and sustainable development goals'
  ];

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % whatWeDo.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + whatWeDo.length) % whatWeDo.length);

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <SEO
        title="Built Environment Technology Solutions — Construction Tech | STRAY Apps"
        description="Digital solutions for the construction and real estate sector — planning intelligence, generative design, BIM automation, and ERP workflow automation."
        path="/industry-solutions/construction-tech"
      />
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          <div className="text-left mb-16">
            <h1 className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-purple-600 bg-clip-text text-transparent mb-4">
              Built Environment Technology Solutions
            </h1>
            <p className="text-xl sm:text-2xl text-gray-700 max-w-3xl font-light">
              Digital Solutions for Construction & Real Estate Sector
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We design and build digital platforms for the built environment ecosystem — supporting developers, architects, engineers, main contractors, and asset owners.
              </p>
              <p className="text-gray-600 text-lg leading-relaxed">
                We help organisations streamline compliance, accelerate design workflows, automate operations, and improve decision-making across the entire building lifecycle.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-8 shadow-lg">
              <h3 className="text-lg font-semibold text-purple-600 mb-4">
                Aligned with Singapore's industry initiatives
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {initiatives.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-gray-700">
                    <div className="w-2 h-2 rounded-full bg-purple-500 flex-shrink-0"></div>
                    <span className="text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Construction Tech Solutions - Floating Tabs */}
      <section className="py-16 bg-gradient-to-b from-white via-purple-50/30 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-left">
            <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">Construction Tech Solutions</h2>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            {solutionTabs.map((tab, index) => (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-2xl p-6 w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)] hover:border-purple-400 hover:shadow-xl transition-all duration-300 shadow-lg"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-500 rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-purple-600 transition-colors">{tab.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{tab.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-800 mb-6">
            Ready to Digitise Your Built Environment Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Whether you're modernising feasibility workflows, automating ERP processes, or implementing AI-enabled BIM systems — we can help.
          </p>
          <Link to="/contact?mode=aec">
            <button className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg text-white font-semibold text-lg transition-all hover:shadow-lg hover:shadow-purple-500/30">
              Get in Touch
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ConstructionTech;
